import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import DatePicker from "react-datepicker";
import { Link } from "react-router";

import {
    fetchAccountPlanMap,
    fetchNetworkDefCompDetails,
    fetchPlanAccumulatorSetup,
    getNetworkDefCompHistFact,
} from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import { toIsoDate } from "../finance/financeShared";
import SharedTable from "./SharedTable";

const limit = 20;

const planOptionLabel = (plan) => {
    const name = plan?.name || "Plan";
    const hcc = plan?.hccId ? ` (${plan.hccId})` : "";
    const status = plan?.statusLabel ? ` · ${plan.statusLabel}` : "";
    return `${name}${hcc}${status}`;
};

const rowsFromNetworkRiders = (setup) => {
    const riders = setup?.networkRiders || [];
    return riders.map((r, i) => ({
        networkDefCompKey: r.networkDefCompKey,
        networkDefCompName: r.networkDefinition?.name || r.name,
        networkDefCompDesc: r.description || r.networkDefinition?.description,
        networkDefCompStatus: r.statusLabel || r.status,
        planHccId: setup?.plan?.hccId,
        planName: setup?.plan?.name,
        _rowKey: `${r.networkDefCompKey || r.key || i}`,
    }));
};

const NetworkDefinitions = () => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [account, setAccount] = useState(null);
    const [planOption, setPlanOption] = useState(null);
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const { ref, inView } = useInView();

    const asOf = toIsoDate(asOfDate);
    const accountKey = account?.additional?.accountKey;
    const planKey = planOption?.value != null ? Number(planOption.value) : null;
    const filterByPlan = planKey != null;

    useEffect(() => {
        setPlanOption(null);
    }, [accountKey]);

    const {
        data: planMap,
        isLoading: mapLoading,
        error: mapError,
    } = useQuery({
        queryKey: ["account-plan-map", accountKey, asOf],
        queryFn: () => fetchAccountPlanMap({ accountKey, asOfDate: asOf }),
        enabled: !!accountKey && !!asOf,
        staleTime: 0,
        retry: false,
    });

    const planOptions = useMemo(() => {
        const byKey = new Map();
        for (const acct of planMap?.accounts || []) {
            for (const p of acct.plans || []) {
                if (p.key == null || byKey.has(Number(p.key))) continue;
                byKey.set(Number(p.key), {
                    value: Number(p.key),
                    label: planOptionLabel(p),
                    additional: p,
                });
            }
        }
        return [...byKey.values()].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );
    }, [planMap]);

    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery({
        queryKey: ["networkDefinitions", limit, debouncedSearch, planKey, asOf],
        queryFn: async ({ pageParam }) => {
            if (filterByPlan) {
                const setup = await fetchPlanAccumulatorSetup({ planKey, asOfDate: asOf });
                if (setup?.error) throw new Error(setup.error);
                let rows = rowsFromNetworkRiders(setup);
                if (debouncedSearch) {
                    const q = debouncedSearch.toLowerCase();
                    rows = rows.filter((r) =>
                        [r.networkDefCompName, r.networkDefCompDesc]
                            .filter(Boolean)
                            .some((v) => String(v).toLowerCase().includes(q)),
                    );
                }
                return { count: rows.length, rows };
            }

            const params = { ...(pageParam || {}), limit };
            if (debouncedSearch) {
                params.condition = "OR";
                params.networkDefCompName = debouncedSearch;
                params.networkDefCompDesc = debouncedSearch;
            }
            return await getNetworkDefCompHistFact(params);
        },
        select: (data) => ({
            count: data.pages?.[0]?.count,
            rows: data.pages.map((page) => page.rows).flatMap((x) => x),
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            if (filterByPlan) return undefined;
            if (!lastPage?.rows?.length || lastPage.rows.length < limit) return undefined;
            return {
                offset: pages.reduce((accum, page) => accum + (page.rows?.length || 0), 0),
            };
        },
        staleTime: 0,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDebouncedSearch(event.target.value?.trim());
        }, 500),
        [],
    );

    const columns = filterByPlan
        ? ["networkDefCompName", "networkDefCompDesc", "networkDefCompStatus", "planName"]
        : ["networkDefCompName", "networkDefCompDesc", "networkDefCompStatus", "versionEffDateKey", "versionExpDateKey"];

    const onDetailHandler = async (row) => {
        const lookup = row.networkDefCompKey != null
            ? { networkDefCompKey: row.networkDefCompKey }
            : row.networkDefCompHistFactKey != null
                ? { networkDefCompHistFactKey: row.networkDefCompHistFactKey }
                : row.networkDefCompName
                    ? { networkDefCompName: row.networkDefCompName }
                    : null;
        if (!lookup) return { errorMessage: "This row has no Network Definition key or name." };
        return await fetchNetworkDefCompDetails({ ...lookup, asOfDate: asOf });
    };

    const clearFilters = () => {
        setAccount(null);
        setPlanOption(null);
        setSearchValue("");
        setDebouncedSearch("");
        setAsOfDate(new Date());
    };

    return (
        <div className="container ">
            <p className="title">Network Definitions  ( {data?.count || 0} )</p>
            <p className="sub-title">
                Benefit-plan Network Definition components set in-network tiers. The table lists every
                component in HRP — including ones not attached to an account. Optionally pick an account
                and plan to see the definition that plan uses.{" "}
                <Link to="/coverage">Plan-centric coverage view</Link>
                {" · "}
                <Link to="/providers/networks">Supplier / benefit networks</Link>
            </p>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                    <AsyncPaginate
                        placeholder="Account (optional)"
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        debounceTimeout={300}
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={setAccount}
                        styles={customStyles}
                        isClearable
                    />
                    {account && (
                        <Select
                            placeholder={
                                mapLoading
                                    ? "Loading plans…"
                                    : planOptions.length
                                        ? "Plan (optional)"
                                        : "No plans for this as-of date"
                            }
                            className="react-select-container large-input"
                            classNamePrefix="react-select"
                            value={planOption}
                            onChange={setPlanOption}
                            options={planOptions}
                            isDisabled={mapLoading || !planOptions.length}
                            isLoading={mapLoading}
                            isClearable
                            styles={customStyles}
                        />
                    )}
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        placeholder="Search name or description..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                    <div style={{ flex: "0 0 160px", width: 160, minWidth: 160 }}>
                        <DatePicker
                            selected={asOfDate}
                            onChange={(d) => setAsOfDate(d || new Date())}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="As of date"
                            todayButton="Today"
                            className="date-picker"
                            wrapperClassName="date-picker-wrapper"
                        />
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p style={{ cursor: "pointer", color: "var(--red)" }} onClick={clearFilters}>
                    Clear filters
                </p>
            </div>

            {!filterByPlan && (
                <p className="sub-title" style={{ padding: "0 0 1rem" }}>
                    Showing the Network Definition catalog. Expired versions are tinted. Select a plan to
                    see only the definition attached to that plan.
                </p>
            )}
            {account && (mapError || planMap?.error) && (
                <span className="danger">
                    Error: {mapError?.message || planMap?.error || "Failed to load account plans"}
                </span>
            )}

            {status === "loading" || status === "pending" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type="network-definitions"
                    columns={columns}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    searchText={debouncedSearch}
                    onDetailHandler={onDetailHandler}
                    ref={ref}
                    noResultsMessage={
                        filterByPlan
                            ? "This plan has no Network Definition rider as of the selected date."
                            : "No Network Definitions match the search."
                    }
                />
            )}
        </div>
    );
};

export default NetworkDefinitions;
